"use client";

import React, { createContext, useContext, useEffect, useState } from "react";
import { Loader } from "@googlemaps/js-api-loader";

interface GoogleMapsContextType {
  isLoaded: boolean;
  loadError: Error | null;
  placesService: google.maps.places.AutocompleteService | null;
  placesDetailsService: google.maps.places.PlacesService | null;
}

const GoogleMapsContext = createContext<GoogleMapsContextType>({
  isLoaded: false,
  loadError: null,
  placesService: null,
  placesDetailsService: null,
});

export const useGoogleMaps = () => useContext(GoogleMapsContext);

interface GoogleMapsProviderProps {
  children: React.ReactNode;
  apiKey: string;
}

export function GoogleMapsProvider({
  children,
  apiKey,
}: GoogleMapsProviderProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadError, setLoadError] = useState<Error | null>(null);
  const [placesService, setPlacesService] =
    useState<google.maps.places.AutocompleteService | null>(null);
  const [placesDetailsService, setPlacesDetailsService] =
    useState<google.maps.places.PlacesService | null>(null);

  useEffect(() => {
    const loader = new Loader({
      apiKey,
      version: "weekly",
      libraries: ["places"],
    });

    loader
      .load()
      .then(() => {
        setPlacesService(new google.maps.places.AutocompleteService());

        const dummyDiv = document.createElement("div");
        setPlacesDetailsService(new google.maps.places.PlacesService(dummyDiv));

        setIsLoaded(true);
      })
      .catch((error) => {
        console.error("Error loading Google Maps:", error);
        setLoadError(error);
      });
  }, [apiKey]);

  return (
    <GoogleMapsContext.Provider
      value={{ isLoaded, loadError, placesService, placesDetailsService }}
    >
      {children}
    </GoogleMapsContext.Provider>
  );
}
